/**
 * Messages API Service
 *
 * Persistent two-way chat between an instructor and a student.
 *
 * Backed by session-manager Sprint 2.C messaging endpoints (mig 028 —
 * `message_threads` / `thread_messages`), mounted at the service root:
 *
 *   GET   /messages/threads              → { threads: Thread[] }
 *   POST  /messages/threads              → { thread }   (start a DM)
 *   GET   /messages/threads/:id          → { thread, messages }
 *   POST  /messages/threads/:id/messages → { message }  (send)
 *   PATCH /messages/threads/:id/read     → { ok }       (mark thread read)
 *
 * The backend speaks a thread/participant model keyed by user ids only — it
 * carries optional bounded participant displayName values, but no participant
 * email or last-message preview. This
 * service maps that shape into the UI's (instructor-centric) Conversation /
 * Message contract: the "student" is whichever participant isn't the current
 * viewer, scoped roster names/emails enrich the view (verified participant
 * display names are a fallback), and
 * read state is derived from the viewer's thread-level `lastReadAt`.
 *
 * For dev mode (REACT_APP_USE_MOCK_DATA=true) we still synthesize plausible
 * multi-turn threads so the UI can be exercised without the backend; the real
 * path no longer depends on the mock store.
 */

import { sessionManagerApi, fetchCurrentUserId, getOidcSub } from './apiClient';
import { mockFallback, USE_MOCK_DATA } from '../lib/mockFallback';
import { getStudents } from './studentsApi';
import { requireArray, requireRecord } from './responseValidation';
import { createRequestId } from '../lib/requestIdentity';

// =====================================================
// TYPES
// =====================================================

export type SenderRole = 'instructor' | 'student' | 'system';

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderRole: SenderRole;
  senderName: string;
  body: string;
  sentAt: string;
  readAt: string | null;
  edited: boolean;
}

export interface Conversation {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  instructorId: string;
  lastMessageAt: string;
  lastMessagePreview: string;
  unreadCount: number;
  status: 'active' | 'archived';
}

export interface ConversationFilters {
  unreadOnly?: boolean;
  studentId?: string;
}

export interface MessagePageOptions {
  before?: string; // ISO timestamp - fetch messages older than this
  beforeId?: string; // exact server anchor, including timestamp ties
  limit?: number; // default 30
}
export interface MessageCursor { before: string; beforeId: string }
export interface MessagePage { messages: Message[]; nextCursor: MessageCursor | null }

// =====================================================
// MOCK DATA GENERATION
// =====================================================

const MOCK_INSTRUCTOR_ID = 'instructor-1';
const MOCK_INSTRUCTOR_NAME = 'Dr. Smith';

interface MockStudent {
  id: string;
  name: string;
  email: string;
}

const MOCK_STUDENTS: MockStudent[] = [
  { id: '1', name: 'John Doe', email: 'john.doe@example.com' },
  { id: '2', name: 'Jane Smith', email: 'jane.smith@example.com' },
  { id: '3', name: 'Bob Johnson', email: 'bob.johnson@example.com' },
  { id: '4', name: 'Alice Brown', email: 'alice.brown@example.com' },
  { id: '5', name: 'Charlie Wilson', email: 'charlie.wilson@example.com' },
  { id: '6', name: 'Diana Lee', email: 'diana.lee@example.com' },
];

// Stable seeded threads keyed by studentId so refetches return the same data.
interface MockThread {
  conversation: Conversation;
  messages: Message[];
}

const MOCK_THREADS: Map<string, MockThread> = new Map();

const STUDENT_PROMPTS = [
  "Hi, I'm stuck on lab 3. The OpenAI client is throwing a 401 even though I set the API key. Any ideas?",
  'I submitted my RAG project but it says pending grade — is that expected?',
  "Thanks for the feedback on the prompt engineering lab! I'm reworking the few-shot section now.",
  'Could you take a look at my code when you get a chance? I think my chain is hitting a recursion issue.',
  'Quick question: are we expected to fine-tune locally or use the proxy endpoint?',
  "I won't be in office hours tomorrow — is there a recording from last week I can watch?",
  'My environment crashed during the eval run. Restarting it now but wanted to flag it.',
  "I think I found a typo in the lab instructions for the LangChain section — happy to send a PR.",
];

const INSTRUCTOR_REPLIES = [
  'Sounds like the env var isn\'t making it into the container. Try `printenv | grep OPENAI` in the terminal — if it\'s empty, hit the "Reload Secrets" button in the IDE.',
  "It is. The autograder runs once an hour; you'll see a score appear in the next cycle.",
  "Glad it helped. Don't forget the system prompt needs to constrain the output schema explicitly.",
  "Send me the file path and I'll pull it up. Likely the recursion guard isn't firing on the empty-tool-result branch.",
  'Use the proxy — it gives you logging + rate limits for free. Local works but you\'ll burn credits faster.',
  'Recording is in the announcements channel from this morning. Watch the first 25 min — the rest is Q&A.',
  'No worries. If the restart doesn\'t help, paste the last 50 lines of the pod log and I\'ll dig in.',
  'Please do — instructions PRs are very welcome.',
];

const SYSTEM_NOTES = [
  'Conversation started',
  'Student joined "LLM Fundamentals" cohort',
  'Lab 3 marked as graded',
];

function pseudoRandom(seed: number): () => number {
  // Mulberry32 — small deterministic PRNG so mock threads are stable per studentId.
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function buildMockThread(student: MockStudent): MockThread {
  const rand = pseudoRandom(hashSeed(student.id));
  const messageCount = 5 + Math.floor(rand() * 11); // 5..15
  const messages: Message[] = [];
  const conversationId = `conv-${student.id}`;

  // First message is always a system "Conversation started" note.
  const firstSentAt = new Date(Date.now() - 1000 * 60 * 60 * 24 * (3 + Math.floor(rand() * 10)));
  messages.push({
    id: `${conversationId}-msg-sys-0`,
    conversationId,
    senderId: 'system',
    senderRole: 'system',
    senderName: 'System',
    body: SYSTEM_NOTES[0],
    sentAt: firstSentAt.toISOString(),
    readAt: firstSentAt.toISOString(),
    edited: false,
  });

  let cursor = firstSentAt.getTime();
  // Alternate sides with some randomness; gaps of 5min..6h.
  let lastRole: SenderRole = 'student';
  for (let i = 1; i < messageCount; i++) {
    cursor += 1000 * 60 * (5 + Math.floor(rand() * 360));
    const role: SenderRole =
      rand() < 0.08 && i > 2 ? 'system' : lastRole === 'student' ? 'instructor' : 'student';
    lastRole = role === 'system' ? lastRole : role;
    const isLatest = i === messageCount - 1;
    const sentAt = new Date(cursor).toISOString();
    let body: string;
    if (role === 'system') {
      body = SYSTEM_NOTES[1 + Math.floor(rand() * (SYSTEM_NOTES.length - 1))];
    } else if (role === 'student') {
      body = STUDENT_PROMPTS[Math.floor(rand() * STUDENT_PROMPTS.length)];
    } else {
      body = INSTRUCTOR_REPLIES[Math.floor(rand() * INSTRUCTOR_REPLIES.length)];
    }
    // The most recent student message is unread by the instructor 60% of the time.
    const readAt =
      role === 'student' && isLatest && rand() < 0.6
        ? null
        : new Date(cursor + 1000 * 60).toISOString();
    messages.push({
      id: `${conversationId}-msg-${i}`,
      conversationId,
      senderId:
        role === 'instructor' ? MOCK_INSTRUCTOR_ID : role === 'student' ? student.id : 'system',
      senderRole: role,
      senderName:
        role === 'instructor' ? MOCK_INSTRUCTOR_NAME : role === 'student' ? student.name : 'System',
      body,
      sentAt,
      readAt,
      edited: role !== 'system' && rand() < 0.07,
    });
  }

  const lastMsg = messages[messages.length - 1];
  const unreadCount = messages.filter(
    (m) => m.senderRole === 'student' && m.readAt === null
  ).length;

  const conversation: Conversation = {
    id: conversationId,
    studentId: student.id,
    studentName: student.name,
    studentEmail: student.email,
    instructorId: MOCK_INSTRUCTOR_ID,
    lastMessageAt: lastMsg.sentAt,
    lastMessagePreview:
      lastMsg.body.length > 90 ? `${lastMsg.body.slice(0, 87)}...` : lastMsg.body,
    unreadCount,
    status: 'active',
  };

  return { conversation, messages };
}

function getOrBuildThread(studentId: string): MockThread {
  let thread = MOCK_THREADS.get(studentId);
  if (!thread) {
    const student =
      MOCK_STUDENTS.find((s) => s.id === studentId) ||
      ({ id: studentId, name: `Student ${studentId}`, email: `student-${studentId}@example.com` } as MockStudent);
    thread = buildMockThread(student);
    MOCK_THREADS.set(studentId, thread);
  }
  return thread;
}

// Seed all default students once so the inbox has a populated list.
function seedMockThreads(): void {
  for (const s of MOCK_STUDENTS) {
    if (!MOCK_THREADS.has(s.id)) {
      MOCK_THREADS.set(s.id, buildMockThread(s));
    }
  }
}

// =====================================================
// BACKEND CONTRACT (session-manager MessagingService shapes)
// =====================================================

interface BackendThreadParticipant {
  userId: string;
  displayName?: string | null;
  role: string;
  joinedAt: string;
  lastReadAt: string | null;
  currentlyOnline?: boolean;
}

interface BackendThread {
  id: string;
  kind: string;
  createdBy: string | null;
  subject: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  participants: BackendThreadParticipant[];
  unreadCount?: number;
}

interface BackendMessage {
  id: string;
  threadId: string;
  senderId: string | null;
  content: string;
  sentAt: string;
  editedAt: string | null;
  replyToMessageId: string | null;
  attachments: unknown[];
}

// =====================================================
// MAPPING HELPERS (backend → UI Conversation/Message)
// =====================================================

// The "viewer" for attribution is the backend DB `users.id` — the value the
// backend stores in `thread_messages.sender_id` and thread participant ids.
// It is NOT the OIDC `sub` (see apiClient.fetchCurrentUserId). The legacy
// zustand authStore `user.id` was always '' under OIDC, which classified every
// message (including the instructor's own) as an incoming student bubble.
async function currentUserId(): Promise<string> {
  const id = await fetchCurrentUserId();
  if (!id && !USE_MOCK_DATA) throw new Error('Could not verify your messaging identity. Please retry.');
  return id;
}

interface StudentLite {
  name: string;
  email: string;
}

// The thread payload has no display names, so we resolve them from the
// instructor students roster. Cached briefly to keep the 30s conversation
// poll from re-fetching the roster every tick.
let studentCache: { subject: string; at: number; map: Map<string, StudentLite> } | null = null;
const STUDENT_CACHE_TTL = 60_000;

async function resolveStudents(): Promise<Map<string, StudentLite>> {
  const subject = getOidcSub();
  if (studentCache?.subject === subject && Date.now() - studentCache.at < STUDENT_CACHE_TTL) {
    return studentCache.map;
  }
  const map = new Map<string, StudentLite>();
  try {
    const roster = await getStudents({ limit: 200 });
    for (const s of roster.data) {
      const name = [s.firstName, s.lastName].filter(Boolean).join(' ').trim() || s.email;
      map.set(s.id, { name, email: s.email });
    }
  } catch {
    // Roster unavailable — mapping falls back to id-derived labels below.
  }
  studentCache = { subject, at: Date.now(), map };
  return map;
}

/** The "student" is whichever participant isn't the viewing instructor. */
function otherParticipantId(thread: BackendThread, viewerId: string): string {
  const participants = thread.participants ?? [];
  const other = participants.find((p) => p.userId !== viewerId) ?? participants[0];
  return other?.userId ?? '';
}

function mapThreadToConversation(
  thread: BackendThread,
  students: Map<string, StudentLite>,
  viewerId: string,
  previewOverride?: string
): Conversation {
  const studentId = otherParticipantId(thread, viewerId);
  const lite = studentId ? students.get(studentId) : undefined;
  const participantName = thread.participants?.find(p => p.userId === studentId)?.displayName;
  const displayName = typeof participantName === 'string' ? participantName.trim().slice(0, 120) : '';
  return {
    id: thread.id,
    studentId,
    studentName: lite?.name ?? (displayName || (studentId ? `Student ${studentId.slice(0, 6)}` : 'Conversation')),
    studentEmail: lite?.email ?? '',
    instructorId: viewerId,
    lastMessageAt: thread.lastMessageAt ?? thread.createdAt,
    lastMessagePreview: previewOverride ?? thread.subject ?? '',
    unreadCount: thread.unreadCount ?? 0,
    status: 'active',
  };
}

function mapMessage(
  m: BackendMessage,
  viewerId: string,
  viewerLastReadAt: string | null,
  students: Map<string, StudentLite>
): Message {
  const isSystem = m.senderId == null;
  const isInstructor = m.senderId === viewerId;
  const senderRole: SenderRole = isSystem ? 'system' : isInstructor ? 'instructor' : 'student';
  // Backend tracks read state per-participant (thread-level lastReadAt), not
  // per-message. A student message is "read" once the viewer's lastReadAt is
  // at/after it; anything newer stays unread so the page re-issues markAsRead.
  const readAt =
    isSystem || isInstructor
      ? m.sentAt
      : viewerLastReadAt && new Date(m.sentAt).getTime() <= new Date(viewerLastReadAt).getTime()
        ? viewerLastReadAt
        : null;
  return {
    id: m.id,
    conversationId: m.threadId,
    senderId: m.senderId ?? 'system',
    senderRole,
    senderName: isSystem
      ? 'System'
      : isInstructor
        ? 'You'
        : (m.senderId ? students.get(m.senderId)?.name : undefined) ?? 'Student',
    body: m.content,
    sentAt: m.sentAt,
    readAt,
    edited: m.editedAt != null,
  };
}

// =====================================================
// API FUNCTIONS
// =====================================================

/**
 * List conversations for the current instructor, newest activity first.
 * `studentId` / `unreadOnly` filters are applied client-side (the backend
 * list endpoint returns the viewer's full inbox).
 */
export async function getConversations(filters: ConversationFilters = {}): Promise<Conversation[]> {
  const viewerId = await currentUserId();
  try {
    const [response, students] = await Promise.all([
      sessionManagerApi.get('/messages/threads'),
      resolveStudents(),
    ]);
    const threads = requireArray<BackendThread>(response.data?.threads, 'message threads');
    threads.forEach((thread) => requireRecord(thread, 'message thread', ['id']));
    let conversations = threads.map((t) => mapThreadToConversation(t, students, viewerId));
    if (filters.studentId) {
      conversations = conversations.filter((c) => c.studentId === filters.studentId);
    }
    if (filters.unreadOnly) {
      conversations = conversations.filter((c) => c.unreadCount > 0);
    }
    conversations.sort(
      (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
    );
    return conversations;
  } catch (error) {
    seedMockThreads();
    let conversations = Array.from(MOCK_THREADS.values()).map((t) => t.conversation);
    if (filters.studentId) {
      conversations = conversations.filter((c) => c.studentId === filters.studentId);
    }
    if (filters.unreadOnly) {
      conversations = conversations.filter((c) => c.unreadCount > 0);
    }
    conversations.sort(
      (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
    );
    return mockFallback(error, conversations);
  }
}

/**
 * Fetch a single conversation by id. Derives the last-message preview from the
 * thread's newest message (the list endpoint carries none).
 */
export async function getConversation(conversationId: string): Promise<Conversation> {
  const viewerId = await currentUserId();
  try {
    const [response, students] = await Promise.all([
      sessionManagerApi.get(`/messages/threads/${conversationId}`),
      resolveStudents(),
    ]);
    const thread = requireRecord(response.data?.thread, 'message thread', ['id']) as unknown as BackendThread;
    const messages = requireArray<BackendMessage>(response.data?.messages, 'messages');
    // Backend returns messages newest-first.
    const preview = messages[0]?.content;
    return mapThreadToConversation(thread, students, viewerId, preview);
  } catch (error) {
    seedMockThreads();
    const thread = Array.from(MOCK_THREADS.values()).find(
      (t) => t.conversation.id === conversationId
    );
    if (!thread) {
      throw error;
    }
    return mockFallback(error, thread.conversation);
  }
}

/**
 * Fetch messages for a conversation, newest first.
 *
 * The server owns the bounded window and exact timestamp/id cursor.
 */
export async function getMessages(
  conversationId: string,
  options: MessagePageOptions = {}
): Promise<Message[]> {
  return (await getMessagePage(conversationId, options)).messages;
}

export async function getMessagePage(conversationId: string, options: MessagePageOptions = {}): Promise<MessagePage> {
  const viewerId = await currentUserId();
  try {
    const [response, students] = await Promise.all([
      sessionManagerApi.get(`/messages/threads/${conversationId}`, { params: {
        limit: options.limit ?? 30, ...(options.before ? { before: options.before, before_id: options.beforeId } : {}),
      } }),
      resolveStudents(),
    ]);
    const thread = requireRecord(response.data?.thread, 'message thread', ['id']) as unknown as BackendThread;
    const backendMessages = requireArray<BackendMessage>(response.data?.messages, 'messages');
    backendMessages.forEach((message) => requireRecord(message, 'message', ['id', 'threadId', 'content', 'sentAt']));
    const viewerLastReadAt =
      (thread.participants ?? []).find((p) => p.userId === viewerId)?.lastReadAt ?? null;
    const pagination = response.data?.pagination;
    let nextCursor: MessageCursor | null = null;
    if (pagination?.hasMore) {
      const cursor = pagination.nextCursor;
      if (!cursor || typeof cursor.before !== 'string' || !Number.isFinite(Date.parse(cursor.before)) ||
          typeof cursor.beforeId !== 'string' || !/^[0-9a-f-]{36}$/i.test(cursor.beforeId)) {
        throw new Error('Invalid message pagination');
      }
      nextCursor = { before: cursor.before, beforeId: cursor.beforeId };
    }
    // Preserve server order: it includes microsecond precision and UUID ties.
    return { messages: backendMessages.map((m) => mapMessage(m, viewerId, viewerLastReadAt, students)), nextCursor };
  } catch (error) {
    const limit = options.limit ?? 30;
    seedMockThreads();
    const thread = Array.from(MOCK_THREADS.values()).find(
      (t) => t.conversation.id === conversationId
    );
    if (!thread) {
      return mockFallback(error, { messages: [], nextCursor: null });
    }
    // Newest-first ordering for client consumption.
    let pool = [...thread.messages].sort(
      (a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime()
    );
    if (options.before) {
      const beforeMs = new Date(options.before).getTime();
      pool = pool.filter((m) => new Date(m.sentAt).getTime() < beforeMs);
    }
    const messages = pool.slice(0, limit);
    const last = messages[messages.length - 1];
    return mockFallback(error, { messages, nextCursor: pool.length > limit && last ? { before: last.sentAt, beforeId: last.id } : null });
  }
}

/**
 * Send a new message in a conversation. Returns the persisted message.
 */
export async function sendMessage(conversationId: string, body: string, clientMessageId = createRequestId()): Promise<Message> {
  const viewerId = await currentUserId();
  try {
    const [response, students] = await Promise.all([
      sessionManagerApi.post(`/messages/threads/${conversationId}/messages`, { content: body.trim(), client_message_id: clientMessageId }),
      resolveStudents(),
    ]);
    const message = requireRecord(response.data?.message, 'saved message', ['id', 'threadId', 'content', 'sentAt']) as unknown as BackendMessage;
    return mapMessage(message, viewerId, new Date().toISOString(), students);
  } catch (error) {
    seedMockThreads();
    const thread = Array.from(MOCK_THREADS.values()).find(
      (t) => t.conversation.id === conversationId
    );
    if (!thread) {
      throw error;
    }
    const now = new Date().toISOString();
    const message: Message = {
      id: `${conversationId}-msg-${thread.messages.length}`,
      conversationId,
      senderId: MOCK_INSTRUCTOR_ID,
      senderRole: 'instructor',
      senderName: MOCK_INSTRUCTOR_NAME,
      body,
      sentAt: now,
      readAt: null,
      edited: false,
    };
    // Mutate the mock store so subsequent reads see the new message.
    thread.messages.push(message);
    thread.conversation.lastMessageAt = now;
    thread.conversation.lastMessagePreview =
      body.length > 90 ? `${body.slice(0, 87)}...` : body;
    return mockFallback(error, message);
  }
}

/**
 * Mark a conversation as read by the current instructor. The backend marks the
 * whole thread read for the viewer (no per-message granularity), so the
 * `messageIds` argument only gates whether a call is needed.
 */
export async function markAsRead(conversationId: string, messageIds: string[]): Promise<void> {
  if (messageIds.length === 0) return;
  try {
    await sessionManagerApi.patch(`/messages/threads/${conversationId}/read`);
  } catch (error) {
    const thread = Array.from(MOCK_THREADS.values()).find(
      (t) => t.conversation.id === conversationId
    );
    if (thread) {
      const now = new Date().toISOString();
      const ids = new Set(messageIds);
      for (const m of thread.messages) {
        if (ids.has(m.id) && !m.readAt) {
          m.readAt = now;
        }
      }
      thread.conversation.unreadCount = thread.messages.filter(
        (m) => m.senderRole === 'student' && m.readAt === null
      ).length;
    }
    return mockFallback(error, undefined);
  }
}

/**
 * Create (or reuse) a direct-message thread with a given student.
 */
export async function startConversation(studentId: string, clientRequestId = createRequestId()): Promise<Conversation> {
  const viewerId = await currentUserId();
  try {
    const [response, students] = await Promise.all([
      sessionManagerApi.post('/messages/threads', {
        kind: 'instructor',
        participant_user_ids: [studentId],
        client_request_id: clientRequestId,
      }),
      resolveStudents(),
    ]);
    const thread = requireRecord(response.data?.thread, 'saved message thread', ['id', 'createdAt']) as unknown as BackendThread;
    requireArray(thread.participants, 'message thread participants');
    return mapThreadToConversation(thread, students, viewerId);
  } catch (error) {
    const thread = getOrBuildThread(studentId);
    return mockFallback(error, thread.conversation);
  }
}
